import React, { useEffect, useState } from "react";

const Settings = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    const storedPreference = localStorage.getItem("theme");
    if (storedPreference === "dark") {
      root.classList.add("dark");
      setIsDarkMode(true);
    } else {
      root.classList.remove("dark");
      setIsDarkMode(false);
    }
  }, []);

  const toggleDarkMode = () => {
    const root = document.documentElement;
    root.classList.toggle("dark");
    document.getElementById("hyman-hive-root").classList.toggle("tw-dark");
    const isNowDark = root.classList.contains("dark");
    setIsDarkMode(isNowDark);
    localStorage.setItem("theme", isNowDark ? "dark" : "light");
  };

  return (
    <div className="tw-flex tw-gap-6 tw-flex-start tw-justify-start tw-flex-col sm:tw-flex-row">
      <div className="tw-w-full">
        <div className="tw-bg-white dark:tw-bg-black tw-text-black dark:tw-text-white dark:tw-text-white tw-px-6 sm:tw-px-10 tw-py-8 sm:tw-py-12 tw-rounded-2xl tw-shadow-sm tw-w-full">
          <div className="tw-flex tw-gap-12 tw-items-start">
            <div className="tw-w-full">
              <p className="tw-text-black dark:tw-text-white tw-text-xl">
                Export My Data
              </p>
              <p className="tw-text-gray-600 dark:tw-text-gray-400 tw-max-w-[80%]">
                Download all of your meal logs and tracking data for personal
                use or backup.
              </p>
            </div>
            <button
              className="tw-cursor-pointer tw-px-6 tw-py-4 tw-rounded-full tw-bg-gray-200 dark:tw-bg-gray-800 hover:tw-bg-gray-300 dark:hover:tw-bg-gray-700 tw-min-w-[96px] tw-text-black dark:tw-text-white dark:tw-text-white"
              onClick={() => {
                const data = JSON.parse(
                  localStorage.getItem("custom.hyman_hive_log")
                );
                if (!data) return;

                let csv =
                  "Day Number,Date,Meal,Protein Name,Grams,Ounces,Notes\n";

                const sortedEntries = Object.entries(data).sort((a, b) => {
                  const dayA = parseInt(a[0].replace("Day ", ""), 10);
                  const dayB = parseInt(b[0].replace("Day ", ""), 10);
                  return dayA - dayB;
                });

                const meals = [
                  "Breakfast",
                  "Snack 1",
                  "Lunch",
                  "Snack 2",
                  "Dinner",
                ];

                sortedEntries.forEach(([day, dayData]) => {
                  const date = dayData.date;
                  const dayNumber = day.replace("Day ", "");
                  meals.forEach((meal) => {
                    const mealEntry = dayData.entries[meal];
                    if (!mealEntry) return;

                    const { notes = "", protein = [] } = mealEntry;
                    protein.forEach(({ item, grams, oz }) => {
                      csv += `${dayNumber},${date},${meal},${item},${grams},${oz},${notes}\n`;
                    });

                    // If no proteins, still write a row with notes
                    if (protein.length === 0 && notes) {
                      csv += `${dayNumber},${date},${meal},,,,,${notes}\n`;
                    }
                  });
                });

                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = "hive_log.csv";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
              }}
            >
              Export
            </button>
          </div>

          <div className="tw-flex tw-gap-6 tw-items-start tw-mt-14">
            <div className="tw-w-full">
              <p className="tw-text-black dark:tw-text-white tw-text-xl">
                Reset Daily Log
              </p>
              <p className="tw-text-gray-600 dark:tw-text-gray-400 tw-max-w-[80%]">
                Clear all your daily entries and start fresh. This action only
                affects the data stored in your browser.
              </p>
            </div>
            <button
              className="tw-cursor-pointer tw-px-6 tw-py-4 tw-rounded-full tw-bg-gray-200 dark:tw-bg-gray-800 hover:tw-bg-gray-300 dark:hover:tw-bg-gray-700 tw-min-w-[96px] tw-text-black dark:tw-text-white dark:tw-text-white"
              onClick={() => {
                localStorage.removeItem("custom.hyman_hive_log");
                localStorage.removeItem("custom.hyman_hive_start_date");
                const snackbar = document.createElement("div");
                snackbar.textContent = "Your daily log has been reset.";
                snackbar.className =
                  "tw-fixed tw-bottom-6 tw-left-1/2 tw-transform -tw-translate-x-1/2 tw-bg-black tw-text-white tw-px-4 tw-py-2 tw-rounded-lg tw-shadow-lg tw-z-50 tw-opacity-90 tw-transition-opacity";
                document.body.appendChild(snackbar);
                setTimeout(() => {
                  snackbar.classList.add("tw-opacity-0");
                  setTimeout(() => {
                    document.body.removeChild(snackbar);
                  }, 300);
                }, 2000);
              }}
            >
              Reset
            </button>
          </div>

          <div className="tw-flex tw-gap-6 tw-items-start tw-mt-14">
            <div className="tw-w-full">
              <p className="tw-text-black dark:tw-text-white tw-text-xl">
                Dark Mode
              </p>
              <p className="tw-text-gray-600 dark:tw-text-gray-400 tw-max-w-[80%]">
                Toggle between light and dark themes to match your environment
                or personal preference.
              </p>
            </div>
            <div className="tw-flex tw-items-center tw-gap-4">
              <label className="tw-relative tw-inline-flex tw-items-center tw-cursor-pointer">
                <input
                  type="checkbox"
                  className="tw-sr-only peer"
                  onChange={toggleDarkMode}
                  checked={isDarkMode}
                />
                <div
                  className={`tw-w-[96px] tw-h-[46px] tw-rounded-full tw-p-[8px] tw-transition-colors ${
                    isDarkMode
                      ? "tw-bg-black dark:tw-bg-white"
                      : "tw-bg-gray-200"
                  } dark:tw-bg-gray-800`}
                >
                  <div
                    className={`tw-w-[31px] tw-h-[31px] tw-rounded-full tw-transition-all tw-transform tw-bg-white dark:tw-bg-black ${
                      isDarkMode ? "tw-translate-x-[50px]" : "tw-translate-x-0"
                    }`}
                  >
                    &nbsp;
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
